'use client';

import { GlobalStoreProvider } from '@repo/store/web';
import { useGlobalStore } from '@repo/store/web';
import type { GlobalState } from '@repo/store/web';
import { authUISlice } from '@/store/slices/auth-ui-slice';
import { webAuthSlice } from '@/store/slices/auth-slice';
import { headerNavSlice } from '@/store/slices/header-nav-slice';
import type { AuthUISlice } from '@/store/slices/auth-ui-slice';
import type { WebAuthSlice } from '@/store/slices/auth-slice';
import type { HeaderNavSlice } from '@/store/slices/header-nav-slice';
import { useMemo, type ReactNode } from 'react';

// Extra slice types contributed by the web app
export type WebExtraSlices = {
  authUI: AuthUISlice;
  webAuth: WebAuthSlice;
  headerNav: HeaderNavSlice;
};

// App-level typed global state (core + web extras)
export type AppGlobalState = GlobalState<WebExtraSlices>;

type PrefetchedSlices = 'webAuth' | 'profile';

export type StoreInitialState = {
  [K in PrefetchedSlices]?: Partial<AppGlobalState[K]>;
};

// App-level typed selector hook
export function useStore<T>(selector: (state: AppGlobalState) => T): T {
  return useGlobalStore<WebExtraSlices, T>(selector);
}

interface StoreProviderProps {
  children: ReactNode;
  initialState?: StoreInitialState | null;
}

export function StoreProvider({ children }: StoreProviderProps) {
  return (
    <GlobalStoreProvider<WebExtraSlices>
      storage={() => {
        if (typeof window !== 'undefined') {
          return {
            getItem: (name: string) => {
              return window?.localStorage?.getItem(name);
            },
            setItem: (name: string, value: string) => {
              window?.localStorage?.setItem(name, value);
            },
            removeItem: (name: string) => {
              window?.localStorage?.removeItem(name);
            },
          };
        }
        return null;
      }}
      // Persist only the preferences namespace (fields filtered by slice.persist.keys if set)
      // persistKeys={['preferences']}
      // Pass preferences as an extra slice (overrides default by name if needed)
      slices={[authUISlice, webAuthSlice, headerNavSlice]}
      enableDevtools={true}
      devtoolsName="WebAppStore"
      onRehydrateStorage={() => {
        return (state, error) => {
          state?.webAuth?.setSessionHydrated(true);
        };
      }}
    >
      {children}
    </GlobalStoreProvider>
  );
}
